import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Text as TextAlpha } from '@oneaudi/unified-web-alpha-components';
import { useTranslation } from 'react-i18next';
import { Text } from '@oneaudi/unified-web-components';
import CheckBox from '../Inputs/CheckboxInput/CheckBox';
import { selectInputs, setUserInputs } from '../../redux/inputsReducer';
import { digitalLeadsFormInput, setModelLinesSelected } from '../../utils/analyticsEventListeners';
import { selectDealerAndVehicleData } from '../../redux/dealerAndVehicleReducer';
import { selectForm } from '../../redux/formReducer';
import { selectAppConfig } from '../../redux/appConfigReducer';
import { FEATURE_APP_DETAILS } from '../../constants';
import { convertToPromotionsType } from '../../functions/convertToPromotionsType';
import { getVehicleForPromotions } from '../../functions/getVehicleForPromotions';

export interface CheckboxOption {
  key: string;
  label: string;
  value: string;
}
interface CheckboxSelectorProps {
  options: CheckboxOption[];
  inputKey: string;
  maxSelection?: number;
}

const SelectorWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
`;

const components = {
  default: Text,
  alphaText: TextAlpha,
};

const isAlpha = !process.env.IS_LEGACY;

const TextComponent = isAlpha ? components.alphaText : components.default;

const CheckboxSelector: React.FC<CheckboxSelectorProps> = ({
  options,
  inputKey,
  maxSelection = 3,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const inputs = useSelector(selectInputs);

  const { vehicle, vehicleByCarline, vehicleByAudiCode } = useSelector(selectDealerAndVehicleData);
  const contactForm = useSelector(selectForm);
  const { offers } = useSelector(selectAppConfig);
  const promotion = convertToPromotionsType(offers!);

  const selectedValues: string[] = useMemo(() => {
    if (Array.isArray(inputs[inputKey])) return inputs[inputKey];
    return [];
  }, [inputKey, inputs]);

  const handleCheckboxChange = useCallback(
    (valueClicked: string, isChecked: boolean) => {
      let newValues: string[];

      if (isChecked) {
        // CASE A: User is trying to select (Check)
        if (selectedValues.length >= maxSelection) {
          return;
        }
        newValues = [...selectedValues, valueClicked];
      } else {
        // CASE B: User is trying to deselect (Uncheck)
        newValues = selectedValues.filter((val) => val !== valueClicked);
      }

      dispatch(
        setUserInputs({
          inputName: inputKey,
          inputValue: newValues,
        }),
      );

      // Update context before firing analytics event
      setModelLinesSelected(newValues.length > 0 ? newValues : undefined);

      const vehicleInView = vehicle || vehicleByCarline || vehicleByAudiCode;
      const vehicleData = vehicleInView || (offers ? getVehicleForPromotions(offers) : undefined);

      const inputTouched = {
        inputTitle: inputKey,
        inputValue: valueClicked,
        action: isChecked ? 'select' : 'deselect',
        inputType: 'checkbox',
      };

      digitalLeadsFormInput(contactForm, vehicleData, FEATURE_APP_DETAILS, promotion, inputTouched);
    },
    [
      dispatch,
      inputKey,
      selectedValues,
      maxSelection,
      vehicle,
      vehicleByCarline,
      vehicleByAudiCode,
      offers,
      contactForm,
      promotion,
    ],
  );

  return (
    <>
      <TextComponent
        tag="p"
        margin={['0px ', '0px', '16px', '0px']}
        variant={{
          type: 'copy',
          order: '2',
          style: 'bold',
        }}
      >
        {t('digitalLeads.inputs.maxModelsSelected', { maxSelection })}
      </TextComponent>
      <SelectorWrapper>
        {options.map((option) => {
          const isChecked = selectedValues.includes(option.value);

          return (
            <CheckBox
              key={option.key}
              inputName={option.key}
              checked={isChecked}
              label={option.label}
              onChange={() => handleCheckboxChange(option.value, !isChecked)}
              ariaLabel={typeof option.label === 'string' ? option.label : option.value}
            />
          );
        })}
      </SelectorWrapper>
    </>
  );
};

export default CheckboxSelector;
